import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/api';
import { Plus, Edit, Trash2, X } from 'lucide-react';

const SuperAdminDashboard = () => {
  const [coupons, setCoupons] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    expiration_date: '',
    applicable_plans: ['starter', 'professional', 'enterprise']
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/api/coupons');
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expiration_date: formData.expiration_date || null,
        applicable_plans: formData.applicable_plans
      };

      if (editingCoupon) {
        await api.patch(`/api/coupons/${editingCoupon.coupon_id}`, payload);
      } else {
        await api.post('/api/coupons', payload);
      }

      fetchCoupons();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      await api.delete(`/api/coupons/${couponId}`);
      fetchCoupons();
    } catch (error) {
      alert('Error deleting coupon');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      max_uses: coupon.max_uses?.toString() || '',
      expiration_date: coupon.expiration_date ? new Date(coupon.expiration_date).toISOString().split('T')[0] : '',
      applicable_plans: coupon.applicable_plans
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      expiration_date: '',
      applicable_plans: ['starter', 'professional', 'enterprise']
    });
    setEditingCoupon(null);
    setShowModal(false);
  };

  const togglePlan = (plan) => {
    setFormData(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(plan)
        ? prev.applicable_plans.filter(p => p !== plan)
        : [...prev.applicable_plans, plan]
    }));
  };

  return (
    <DashboardLayout title="Super Admin Dashboard">
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="voice-card" style={{ background: 'var(--accent-blue-200)' }}>
          <div className="heading-2 mb-2">{coupons.length}</div>
          <div className="body-small">Total Coupons</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-green-200)' }}>
          <div className="heading-2 mb-2">{coupons.filter(c => c.is_active).length}</div>
          <div className="body-small">Active Coupons</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-orange-200)' }}>
          <div className="heading-2 mb-2">{coupons.reduce((sum, c) => sum + c.current_uses, 0)}</div>
          <div className="body-small">Total Uses</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-purple-200)' }}>
          <div className="heading-2 mb-2">{coupons.filter(c => c.discount_type === 'percentage').length}</div>
          <div className="body-small">% Discounts</div>
        </div>
      </div>

      {/* Create Button */}
      <div className="mb-6">
        <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          Create New Coupon
        </button>
      </div>

      {/* Coupons List */}
      <div className="voice-card">
        <h2 className="heading-3 mb-4">All Coupons</h2>
        {loading ? (
          <p>Loading...</p>
        ) : coupons.length === 0 ? (
          <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>No coupons yet. Create your first one!</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                  <th className="text-left p-3 body-small font-semibold">Code</th>
                  <th className="text-left p-3 body-small font-semibold">Type</th>
                  <th className="text-left p-3 body-small font-semibold">Value</th>
                  <th className="text-left p-3 body-small font-semibold">Uses</th>
                  <th className="text-left p-3 body-small font-semibold">Plans</th>
                  <th className="text-left p-3 body-small font-semibold">Status</th>
                  <th className="text-left p-3 body-small font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.coupon_id} className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <td className="p-3">
                      <span className="mono-text font-semibold">{coupon.code}</span>
                    </td>
                    <td className="p-3 capitalize">{coupon.discount_type}</td>
                    <td className="p-3">
                      {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}
                    </td>
                    <td className="p-3">
                      {coupon.current_uses} {coupon.max_uses ? `/ ${coupon.max_uses}` : '/ ∞'}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {coupon.applicable_plans.map(plan => (
                          <span key={plan} className="px-2 py-1 rounded text-xs" style={{ background: 'var(--accent-blue-200)' }}>
                            {plan}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(coupon)} className="p-2 rounded hover:bg-blue-50">
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(coupon.coupon_id)} className="p-2 rounded hover:bg-red-50">
                          <Trash2 size={16} style={{ color: '#DC2626' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={resetForm}>
          <div className="voice-card max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="heading-3">{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</h2>
              <button onClick={resetForm} className="p-2 rounded hover:bg-gray-100">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="body-small font-semibold mb-2 block">Coupon Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  className="w-full p-3 rounded-lg border mono-text"
                  style={{ borderColor: 'var(--border-light)' }}
                  placeholder="LAUNCH50"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="body-small font-semibold mb-2 block">Discount Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    className="w-full p-3 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="body-small font-semibold mb-2 block">
                    Discount Value {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    required
                    className="w-full p-3 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder={formData.discount_type === 'percentage' ? '50' : '5000'}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="body-small font-semibold mb-2 block">Max Uses (optional)</label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    className="w-full p-3 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                <div>
                  <label className="body-small font-semibold mb-2 block">Expiration Date (optional)</label>
                  <input
                    type="date"
                    value={formData.expiration_date}
                    onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                    className="w-full p-3 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="body-small font-semibold mb-2 block">Applicable Plans</label>
                <div className="flex gap-4">
                  {['starter', 'professional', 'enterprise'].map(plan => (
                    <label key={plan} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicable_plans.includes(plan)}
                        onChange={() => togglePlan(plan)}
                        className="w-4 h-4"
                      />
                      <span className="capitalize body-small">{plan}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <button type="submit" className="btn-primary flex-1">
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;